import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  itemName?: string; // "게시물", "프로젝트" 등
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
};

/**
 * 삭제 확인 모달
 * DSModal과 다른 디자인이 필요해서 별도로 만든 컴포넌트
 */
export default function DSDeleteConfirmModal({ open, itemName = '', onConfirm, onCancel, isLoading }: Props) {
  const { t } = useTranslation('translation');
  const resolvedItemName = itemName || t('common.deleteConfirm.defaultItemName');

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel, isLoading]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      onPointerDown={isLoading ? undefined : onCancel}
    >
      <Dialog onPointerDown={(e) => e.stopPropagation()}>
        <Title>{t('common.deleteConfirm.title', { itemName: resolvedItemName })}</Title>
        <Description>{t('common.deleteConfirm.description')}</Description>
        <Footer>
          <SecondaryButton onClick={onCancel} disabled={isLoading}>{t('common.actions.no')}</SecondaryButton>
          <PrimaryButton onClick={onConfirm} disabled={isLoading}>
            {isLoading ? t('common.actions.processing') : t('common.actions.yes')}
          </PrimaryButton>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body,
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
`;

const Dialog = styled.div`
  display: flex;
  width: 320px;
  padding: 30px 20px 20px 20px;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  border-radius: 20px;
  background: #FFF;
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.35);
`;

const Title = styled.div`
  color: #17171b;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  text-align: center;
`;

const Description = styled.div`
  color: #17171B;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  text-align: center;
`;

const Footer = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
`;

const SecondaryButton = styled.button`
  display: flex;
  width: 134px;
  height: 48px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 12px;
  border: none;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  background: #DFDFDF;
  color: #17171B;
  transition: background 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  width: 134px;
  height: 48px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 12px;
  background: #026478;
  border: none;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  color: #FFF;
  transition: background 0.2s;
`;

